/**
 * Overflow up to this many px only clips a trigger's side padding, never its
 * label. Showing the scroll arrow then covered real text instead (3px on the
 * Benachrichtigungen tabs at 390px, DEV-542).
 */
const OVERFLOW_EPSILON_PX = 8;

export function tabListHasOverflow(
  scrollWidth: number,
  clientWidth: number,
): boolean {
  return scrollWidth > clientWidth + OVERFLOW_EPSILON_PX;
}

export function tabListCanScrollLeft(scrollLeft: number): boolean {
  return scrollLeft > OVERFLOW_EPSILON_PX;
}

export function tabListCanScrollRight(
  scrollLeft: number,
  scrollWidth: number,
  clientWidth: number,
): boolean {
  return (
    tabListHasOverflow(scrollWidth, clientWidth) &&
    scrollLeft < scrollWidth - clientWidth - OVERFLOW_EPSILON_PX
  );
}

export function nextScrollLeftAfterWheel({
  scrollLeft,
  scrollWidth,
  clientWidth,
  deltaX,
  deltaY,
}: {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
  deltaX: number;
  deltaY: number;
}): { scrollLeft: number; consumed: boolean } {
  if (!tabListHasOverflow(scrollWidth, clientWidth)) {
    return { scrollLeft, consumed: false };
  }

  const delta = Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY;
  if (delta === 0) {
    return { scrollLeft, consumed: false };
  }

  const max = scrollWidth - clientWidth;
  const next = Math.max(0, Math.min(max, scrollLeft + delta));
  return { scrollLeft: next, consumed: next !== scrollLeft };
}

export function nextScrollLeftAfterNudge({
  scrollLeft,
  scrollWidth,
  clientWidth,
  direction,
}: {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
  direction: 'left' | 'right';
}): number {
  const step = Math.max(120, Math.round(clientWidth * 0.65));
  const max = Math.max(0, scrollWidth - clientWidth);
  const delta = direction === 'left' ? -step : step;
  return Math.max(0, Math.min(max, scrollLeft + delta));
}

export function scrollLeftToRevealTab({
  tabOffsetLeft,
  tabOffsetWidth,
  scrollLeft,
  clientWidth,
  edgePadding = 24,
}: {
  tabOffsetLeft: number;
  tabOffsetWidth: number;
  scrollLeft: number;
  clientWidth: number;
  edgePadding?: number;
}): number {
  const max = Math.max(0, tabOffsetLeft);
  if (tabOffsetWidth >= clientWidth) {
    return max;
  }

  const tabStart = tabOffsetLeft;
  const tabEnd = tabOffsetLeft + tabOffsetWidth;
  const viewStart = scrollLeft;
  const viewEnd = scrollLeft + clientWidth;

  if (tabStart < viewStart + edgePadding) {
    return Math.max(0, tabStart - edgePadding);
  }
  if (tabEnd > viewEnd - edgePadding) {
    return Math.max(0, tabEnd - clientWidth + edgePadding);
  }
  return scrollLeft;
}
